/**
 * F10.3 Observability client: JS error + unhandled rejection + network failure capture.
 * PRD-OPP-0015 FR-005: Observability (errors, missing assets).
 * - window.onerror, window.onunhandledrejection
 * - Resource load failures (script/image where feasible)
 * - Network failures for funnel-critical endpoints (join, cart, search, activation)
 * - assets_required_missing: listen for CustomEvent('assets_required_missing') from layout/fallback
 * - PII redaction; common context (release_version, page_type, member_state, etc.)
 * - Sends via CustomEvent('pma:observability') + optional window.PMA_Observability_send(payload)
 * Ref: docs/analytics/f10.3_observability_requirements.md, PRD.v2 §22399–23006, PRD-OPP-0015.
 */
(function () {
  "use strict";

  var CONTEXT_KEY = "PMA_Analytics_Context";
  var ANON_ID_KEY = "pma_anonymous_id";
  var SESSION_ID_KEY = "pma_session_id";
  var TRUNCATE_LEN = 200;
  var MAX_STACK_FRAMES = 10;

  function getContext() {
    try {
      return typeof window[CONTEXT_KEY] === "object" ? window[CONTEXT_KEY] : {};
    } catch {
      return {};
    }
  }

  function uuidv4() {
    try {
      if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
    } catch {}
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
      var r = (Math.random() * 16) | 0;
      var v = c === "x" ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  function getOrCreateId(key) {
    try {
      if (typeof localStorage === "undefined") return uuidv4();
      var id = localStorage.getItem(key);
      if (id) return id;
      id = uuidv4();
      localStorage.setItem(key, id);
      return id;
    } catch {
      return uuidv4();
    }
  }

  function getDeviceType() {
    try {
      var ua = typeof navigator !== "undefined" ? navigator.userAgent || "" : "";
      var w = typeof window !== "undefined" && window.innerWidth;
      if (/tablet|ipad/i.test(ua)) return "tablet";
      if (/mobile|android|iphone|ipod/i.test(ua) || (w && w < 768)) return "mobile";
      return "desktop";
    } catch {
      return "desktop";
    }
  }

  function getMemberState() {
    try {
      var body = typeof document !== "undefined" && document.body;
      if (!body) return "unknown";
      if (body.classList.contains("is-member-active")) return "member";
      if (body.classList.contains("is-member-locked")) return "non_member";
      if (body.classList.contains("is-member-pending-approval")) return "non_member";
      if (body.classList.contains("is-member-denied")) return "non_member";
      return "unknown";
    } catch {
      return "unknown";
    }
  }

  function getApprovalState() {
    try {
      var body = typeof document !== "undefined" && document.body;
      if (!body) return "unknown";
      if (body.classList.contains("is-member-pending-approval")) return "pending";
      if (body.classList.contains("is-member-denied")) return "rejected";
      if (body.classList.contains("is-member-active")) return "approved";
      return "not_required";
    } catch {
      return "unknown";
    }
  }

  /** PII redaction: strip email/phone patterns, truncate. §5.6 */
  function redactString(s) {
    if (s == null || typeof s !== "string") return "";
    var t = s.trim();
    t = t.replace(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g, "[email]");
    t = t.replace(/\b\d{3}[-.]?\d{3}[-.]?\d{4}\b/g, "[phone]");
    return t.length > TRUNCATE_LEN ? t.slice(0, TRUNCATE_LEN) : t;
  }

  function truncateStack(stack) {
    if (stack == null || typeof stack !== "string") return "";
    var lines = stack.split("\n").slice(0, MAX_STACK_FRAMES);
    return lines.map(function (line) { return redactString(line); }).join("\n");
  }

  function buildCommonContext() {
    var ctx = getContext();
    var loc = typeof window !== "undefined" && window.location;
    return {
      page_url: (loc && loc.href) ? redactString(String(loc.href).split("?")[0]) : "",
      page_type: ctx.page_type || "page",
      template: ctx.template || "",
      release_version: (ctx.release_version && String(ctx.release_version).trim()) ? String(ctx.release_version).trim().slice(0, 64) : "",
      device_type: getDeviceType(),
      user_agent: typeof navigator !== "undefined" && navigator.userAgent ? String(navigator.userAgent).slice(0, 256) : "",
      session_id: getOrCreateId(SESSION_ID_KEY),
      anonymous_id: getOrCreateId(ANON_ID_KEY),
      member_state: getMemberState(),
      approval_state: getApprovalState(),
      consent_state: ctx.consent_state || "unknown"
    };
  }

  /** Endpoint categories for network failure grouping. R-F10.3-020 */
  function getEndpointCategory(url) {
    if (!url || typeof url !== "string") return "other";
    var u = url.toLowerCase();
    if (u.indexOf("/cart") !== -1 || u.indexOf("cart/add") !== -1 || u.indexOf("cart/change") !== -1) return "cart";
    if (u.indexOf("/search") !== -1 || u.indexOf("predictive_search") !== -1 || u.indexOf("search/suggest") !== -1) return "search";
    if (u.indexOf("join") !== -1 || u.indexOf("application") !== -1 || u.indexOf("membership") !== -1) return "join";
    if (u.indexOf("activation") !== -1 || u.indexOf("member/verify") !== -1) return "activation";
    if (u.indexOf("contact") !== -1 || u.indexOf("support") !== -1) return "support";
    return "other";
  }

  /** §5.4 Sampling: 0–1 from context; default 1 (full). Apply before send. */
  function getSampleRate() {
    try {
      var ctx = getContext();
      var r = ctx.observability_sample_rate;
      if (r == null) return 1;
      var n = Number(r);
      if (n >= 0 && n <= 1) return n;
      return 1;
    } catch { return 1; }
  }

  function shouldSend() {
    var rate = getSampleRate();
    if (rate >= 1) return true;
    return Math.random() < rate;
  }

  /** §5.3 Group key for errors: type + message signature + first stack line. */
  function errorGroupKey(type, message, stack) {
    var msg = (message || "").replace(/\s+/g, " ").replace(/[^a-zA-Z0-9 ]/g, "").slice(0, 80);
    var firstLine = (stack || "").split("\n")[1] || "";
    firstLine = firstLine.replace(/\s+/g, " ").slice(0, 120);
    return type + "|" + msg + "|" + firstLine;
  }

  /** GH-6519 severity mapping: P0 cart/join/nav, P1 search/filter/PDP, P2 other. */
  function inferSeverity(payload) {
    var et = String(payload.error_type || "").toLowerCase();
    var cat = String(payload.endpoint_category || "").toLowerCase();
    var pt = String(payload.page_type || "").toLowerCase();
    var kind = String(payload.kind || "").toLowerCase();
    if (cat === "cart" || cat === "join" || cat === "activation") return "P0";
    if (
      et.indexOf("cart") !== -1
      || et.indexOf("join") !== -1
      || et.indexOf("checkout") !== -1
      || et.indexOf("nav") !== -1
      || pt === "cart"
      || pt === "join"
    ) return "P0";
    if (
      cat === "search"
      || et.indexOf("search") !== -1
      || et.indexOf("filter") !== -1
      || et.indexOf("predictive") !== -1
      || et.indexOf("commerce_") !== -1
      || pt === "search"
      || pt === "product"
      || pt === "collection"
    ) return "P1";
    if (kind === "resource_failure" || kind === "dispatch_telemetry") return "P2";
    return "P2";
  }

  function send(payload) {
    if (!shouldSend()) return;
    if (!payload.severity_class) payload.severity_class = inferSeverity(payload);
    try {
      document.dispatchEvent(new CustomEvent("pma:observability", { detail: payload, bubbles: true }));
    } catch {}
    try {
      if (typeof window.PMA_Observability_send === "function") {
        window.PMA_Observability_send(payload);
      }
    } catch {}
  }

  function captureError(type, data) {
    var common = buildCommonContext();
    var message = redactString(data.message || "");
    var stack = truncateStack(data.stack);
    var payload = {
      kind: "error",
      error_type: type,
      error_name: data.name || "",
      message: message,
      stack: stack,
      script_url: data.filename ? redactString(data.filename) : undefined,
      lineno: data.lineno,
      colno: data.colno,
      group_key: errorGroupKey(type, message, stack),
      timestamp: new Date().toISOString()
    };
    for (var k in common) payload[k] = common[k];
    send(payload);
  }

  function onError(message, source, lineno, colno, error) {
    captureError("uncaught", {
      name: error && error.name,
      message: message || (error && error.message),
      stack: error && error.stack,
      filename: source,
      lineno: lineno,
      colno: colno
    });
    return false;
  }

  function onUnhandledRejection(event) {
    var reason = event && event.reason;
    var name = (reason && reason.name) || "UnhandledRejection";
    var message = (reason && (reason.message || String(reason))) || "Unknown";
    var stack = reason && reason.stack;
    captureError("unhandled_rejection", { name: name, message: message, stack: stack });
  }

  /** Resource load failures: listen for error on script/img. Best-effort. */
  function captureResourceFailure(el) {
    if (!el || !el.tagName) return;
    var tag = el.tagName.toLowerCase();
    var src = (el.src || el.href || "").trim();
    if (!src) return;
    var common = buildCommonContext();
    var payload = {
      kind: "resource_failure",
      resource_tag: tag,
      resource_url: redactString(src).slice(0, 512),
      timestamp: new Date().toISOString()
    };
    for (var k in common) payload[k] = common[k];
    send(payload);
  }

  function observeResourceFailures() {
    document.addEventListener("error", function (e) {
      if (e.target && (e.target.tagName === "SCRIPT" || e.target.tagName === "IMG")) {
        captureResourceFailure(e.target);
      }
    }, true);
  }

  /** Network: wrap fetch and XMLHttpRequest to capture failures for funnel-critical URLs. */
  var endpointCategories = ["join", "cart", "search", "activation", "support"];
  function shouldCaptureNetworkFailure(url) {
    var cat = getEndpointCategory(url);
    return endpointCategories.indexOf(cat) !== -1 || cat === "other";
  }

  function captureNetworkFailure(details) {
    var common = buildCommonContext();
    var cat = details.endpoint_category || "other";
    var status = details.status || 0;
    var ft = details.failure_type || "unknown";
    var payload = {
      kind: "network_failure",
      endpoint_category: cat,
      url_safe: redactString(details.url).slice(0, 256),
      status: status,
      failure_type: ft,
      group_key: cat + "|" + status + "|" + ft,
      timestamp: new Date().toISOString()
    };
    if (details.request_id) payload.correlation_id = details.request_id;
    for (var k in common) payload[k] = common[k];
    send(payload);
  }

  function generateRequestId() {
    return "pma-" + Date.now().toString(36) + "-" + Math.random().toString(36).slice(2, 10);
  }

  if (typeof window.fetch !== "undefined") {
    var origFetch = window.fetch;
    window.fetch = function (input, init) {
      var url = typeof input === "string" ? input : (input && input.url) || "";
      if (!shouldCaptureNetworkFailure(url)) return origFetch.apply(this, arguments);
      var requestId = generateRequestId();
      var opts = init || {};
      var headers = new Headers(opts.headers || {});
      headers.set("x-pma-request-id", requestId);
      var newInit = Object.assign({}, init, { headers: headers });
      return origFetch.apply(this, [input, newInit]).then(
        function (res) {
          if (!res.ok && res.status >= 400) {
            captureNetworkFailure({
              url: url,
              endpoint_category: getEndpointCategory(url),
              status: res.status,
              failure_type: res.status >= 500 ? "server_5xx" : "server_4xx",
              request_id: requestId
            });
          }
          return res;
        },
        function (err) {
          captureNetworkFailure({
            url: url,
            endpoint_category: getEndpointCategory(url),
            status: 0,
            failure_type: "network",
            request_id: requestId
          });
          throw err;
        }
      );
    };
  }

  if (typeof window.XMLHttpRequest !== "undefined") {
    var OrigXHR = window.XMLHttpRequest;
    window.XMLHttpRequest = function () {
      var xhr = new OrigXHR();
      var reqUrl = "";
      var reqId = generateRequestId();
      var open = xhr.open;
      xhr.open = function (method, url) {
        reqUrl = url;
        open.apply(this, arguments);
        try { xhr.setRequestHeader("x-pma-request-id", reqId); } catch {}
      };
      xhr.addEventListener("load", function () {
        if (xhr.status >= 400 && shouldCaptureNetworkFailure(reqUrl)) {
          captureNetworkFailure({
            url: reqUrl,
            endpoint_category: getEndpointCategory(reqUrl),
            status: xhr.status,
            failure_type: xhr.status >= 500 ? "server_5xx" : "server_4xx",
            request_id: reqId
          });
        }
      });
      xhr.addEventListener("error", function () {
        if (shouldCaptureNetworkFailure(reqUrl)) {
          captureNetworkFailure({
            url: reqUrl,
            endpoint_category: getEndpointCategory(reqUrl),
            status: 0,
            failure_type: "network",
            request_id: reqId
          });
        }
      });
      return xhr;
    };
  }

  /** R-F10.3-002: Accept error reports from error-boundary wrappers (e.g. commerce, predictive). Skip if already has context (our own send). */
  document.addEventListener("pma:observability", function (e) {
    var d = e && e.detail;
    if (!d || d.kind !== "error" || d.page_url) return;
    var common = buildCommonContext();
    var payload = {
      kind: "error",
      error_type: d.error_type || "boundary",
      message: redactString(d.message || ""),
      timestamp: new Date().toISOString()
    };
    for (var k in common) payload[k] = common[k];
    send(payload);
  });

  /** R-F10.3-040: Dispatch health telemetry (queued, sent, blocked_consent, failed). */
  function initDispatchTelemetry() {
    document.addEventListener("pma:analytics:dispatch", function (e) {
      var d = e && e.detail;
      if (!d || !d.type) return;
      var common = buildCommonContext();
      var payload = {
        kind: "dispatch_telemetry",
        dispatch_type: d.type,
        event_name: d.event_name || "",
        reason: d.error || undefined,
        timestamp: new Date().toISOString()
      };
      for (var k in common) payload[k] = common[k];
      send(payload);
    });
  }

  /** PRD-OPP-0002: Required script missing (layout fallback injected). Detail: route, release_version, member_state only (no PII). */
  function initAssetsRequiredMissing() {
    window.addEventListener("assets_required_missing", function (e) {
      var d = e && e.detail;
      if (!d) return;
      var common = buildCommonContext();
      var payload = {
        kind: "assets_required_missing",
        route: (d.route && String(d.route).slice(0, 128)) || "",
        release_version: (d.release_version && String(d.release_version).slice(0, 64)) || "",
        member_state: (d.member_state && String(d.member_state).slice(0, 32)) || "",
        timestamp: new Date().toISOString()
      };
      for (var k in common) payload[k] = common[k];
      send(payload);
    });
  }

  function init() {
    window.onerror = onError;
    window.addEventListener("unhandledrejection", onUnhandledRejection);
    observeResourceFailures();
    initDispatchTelemetry();
    initAssetsRequiredMissing();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
